import { getConfig } from '@/lib/config'
import { getCookieValue } from '@/lib/cookie'
import { Request } from '@/service/request'
import type {
  CreateUserPartnerAssociationOutput,
  SetPartnerCookieByCodeOutput,
  SetPartnerCookieByIdOutput,
  SetPrimaryPartnerOutput,
} from '@/shared/api-generated-types'
import { initUrqlClient } from '@/urql/initializeClient'
import * as Sentry from '@sentry/nextjs'
import * as s from 'superstruct'
import { gql } from 'urql'

const {
  publicRuntimeConfig: {
    graphqlApi: { url },
  },
} = getConfig()

interface PartnerCookie {
  id: string
  code: string
  slug: string
  name?: string // deprecated, may not be present in new cookies
}

const PartnerMetadataSchema = s.record(s.string(), s.record(s.string(), s.unknown()))
export declare const PartnerMetadata: s.Struct<Record<string, Record<string, unknown>>, null>
export interface PartnerMetadata extends s.Infer<typeof PartnerMetadata> {}

const PARTNER_COOKIE_NAME = 'auth.partner'
const PARTNER_COOKIE_FIELDS: (keyof PartnerCookie)[] = ['id', 'code', 'slug']

export const PARTNER_METADATA_COOKIE = 'auth.partner.metadata'

export function getPartnerCookie(req: Request): PartnerCookie | null {
  const unparsedCookie = req.cookies && req.cookies[PARTNER_COOKIE_NAME]

  if (!unparsedCookie) return null

  try {
    const cookie: PartnerCookie = JSON.parse(unparsedCookie)
    if (
      !PARTNER_COOKIE_FIELDS.every((field) => {
        return cookie[field]
      })
    ) {
      return null
    }

    return cookie
  } catch (e) {
    return null
  }
}

export function getPartnerMetadata(req: Request): string | undefined {
  return req.cookies ? req.cookies[PARTNER_METADATA_COOKIE] : undefined
}

const SetPartnerCookieByIdMutation = gql`
  mutation setPartnerCookieById($partnerId: String!) {
    setPartnerCookieById(partnerId: $partnerId) {
      __typename
      success
    }
  }
`

export async function setPartnerCookieById(partnerId?: string): Promise<SetPartnerCookieByIdOutput> {
  if (!partnerId || typeof partnerId !== 'string') {
    return { __typename: 'SetPartnerCookieByIdOutput', success: false }
  }
  try {
    const { urqlClient } = initUrqlClient({})
    const result = await urqlClient.mutation(SetPartnerCookieByIdMutation, { partnerId }).toPromise()

    if (result.error) {
      throw new Error(result.error.message)
    }

    return result.data.setPartnerCookieById
  } catch (err) {
    // Log this error to sentry but dont panic.
    console.error('Failed to do set partner from gsu-web', err)
    Sentry.captureException(err)
    return { __typename: 'SetPartnerCookieByIdOutput', success: false }
  }
}

const SetPartnerCookieByCodeMutation = gql`
  mutation setPartnerCookieByCode($code: String!) {
    setPartnerCookieByCode(code: $code) {
      __typename
      success
    }
  }
`

export async function setPartnerCookieByCode(code?: string | string[]): Promise<SetPartnerCookieByCodeOutput> {
  if (!code || typeof code !== 'string') {
    return { __typename: 'SetPartnerCookieByCodeOutput', success: false }
  }
  try {
    const { urqlClient } = initUrqlClient({})
    const result = await urqlClient.mutation(SetPartnerCookieByCodeMutation, { code }).toPromise()

    if (result.error) {
      throw new Error(result.error.message)
    }

    return result.data.setPartnerCookieByCode
  } catch (err) {
    // Log this error to sentry but dont panic.
    console.error('Failed to do set partner from gsu-web', err)
    Sentry.captureException(err)
    return { __typename: 'SetPartnerCookieByCodeOutput', success: false }
  }
}

export function getPartnerMetadataFromInput(input: string | null): PartnerMetadata | undefined {
  try {
    if (input) {
      const metadata = JSON.parse(input)
      return s.validate(metadata, PartnerMetadataSchema, { coerce: true }) ? metadata : undefined
    }
    return
  } catch (error) {
    console.error('getPartnerMetadataFromInputError', error)
    return
  }
}

export function getPartnerMetadataFromCookieUsingPartnerId(partnerId: string): Record<string, unknown> | undefined {
  const metadataCookie = getCookieValue(PARTNER_METADATA_COOKIE)
  const metadataCookieValue = getPartnerMetadataFromInput(metadataCookie)
  return metadataCookieValue ? metadataCookieValue[partnerId] : undefined
}

export async function setPartnerPrimary(partnerId: string, userId: string): Promise<boolean> {
  if (!partnerId || !userId) return false
  try {
    const mutationBody = {
      query: `
        mutation setPrimaryPartner($input: SetPrimaryPartnerInput!) {
          setPrimaryPartner(input: $input) { __typename success }
        }
      `,
      variables: {
        input: {
          partnerId,
          userId,
          setPartnerCookie: true,
        },
      },
    }
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(mutationBody),
    })
    const parsedResponse = (await response.json()) as {
      data: { setPrimaryPartner: SetPrimaryPartnerOutput }
    }
    return parsedResponse.data.setPrimaryPartner.success
  } catch (err) {
    // Log this error to sentry but dont panic.
    Sentry.captureException(err)
    return false
  }
}

export async function createUserPartnerAssociation(partnerSlug: string): Promise<boolean> {
  if (!partnerSlug) return false
  try {
    const mutationBody = {
      query: `
        mutation createUserPartnerAssociation($partnerSlug: String!) {
          createUserPartnerAssociation(partnerSlug: $partnerSlug) {
            __typename success
          }
        }
      `,
      variables: {
        partnerSlug,
      },
    }
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(mutationBody),
    })
    const parsedResponse = (await response.json()) as {
      data: { createUserPartnerAssociation: CreateUserPartnerAssociationOutput }
    }
    return parsedResponse.data.createUserPartnerAssociation.success
  } catch (err) {
    // Log this error to sentry but dont panic.
    Sentry.captureException(err)
    return false
  }
}
